import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

// Base Card component
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: 'default' | 'bordered' | 'elevated';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className = '', variant = 'default', padding = 'none', children, ...props }, ref) => {
    const baseClasses = 'rounded-lg bg-white dark:bg-gray-800';
    const variantClasses = {
      default: 'border border-gray-200 dark:border-gray-700',
      bordered: 'border-2 border-gray-300 dark:border-gray-600',
      elevated: 'shadow-lg border border-gray-100 dark:border-gray-700',
    };
    const paddingClasses = {
      none: '',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
    };

    return (
      <div
        ref={ref}
        className={`${baseClasses} ${variantClasses[variant]} ${paddingClasses[padding]} ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// CardHeader component - US-075: Enhanced with title/description/action props for responsive layouts
// Using Omit to exclude the native HTML 'title' attribute since we're redefining it
export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  children?: ReactNode;
  borderBottom?: boolean;
  /** Title can be a string or ReactNode for complex headers */
  title?: string | ReactNode;
  /** Description text shown below title */
  description?: string;
  /** Action element (buttons, badges) shown on the right side */
  action?: ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className = '', borderBottom = true, children, title, description, action, ...props }, ref) => {
    // If title/action props are used, render structured layout
    // Otherwise fall back to children for backwards compatibility
    const hasStructuredLayout = title || action;

    return (
      <div
        ref={ref}
        className={`px-4 sm:px-6 py-4 ${borderBottom ? 'border-b border-gray-200 dark:border-gray-700' : ''} ${className}`}
        {...props}
      >
        {hasStructuredLayout ? (
          // US-075: Responsive header layout - stacks on mobile, row on larger screens
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              {typeof title === 'string' ? (
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {title}
                </h3>
              ) : (
                title
              )}
              {description && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {description}
                </p>
              )}
            </div>
            {action && (
              <div className="flex-shrink-0">
                {action}
              </div>
            )}
          </div>
        ) : (
          children
        )}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// CardTitle component (shadcn-style)
export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  children: ReactNode;
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className = '', children, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={`text-lg font-semibold text-gray-900 dark:text-gray-100 ${className}`}
        {...props}
      >
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = 'CardTitle';

// CardBody component (alias for CardContent) - US-075: Responsive padding
export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className = '', children, ...props }, ref) => {
    return (
      // US-075: Reduced horizontal padding on mobile (px-4) vs desktop (sm:px-6)
      <div ref={ref} className={`px-4 sm:px-6 py-4 ${className}`} {...props}>
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

// CardContent component (shadcn-style alias)
export const CardContent = CardBody;
export type CardContentProps = CardBodyProps;

// CardFooter component - US-075: Responsive padding and alignment options
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  borderTop?: boolean;
  /** Footer content alignment - 'left' | 'center' | 'right' (default: 'right') */
  align?: 'left' | 'center' | 'right';
}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className = '', borderTop = true, align = 'right', children, ...props }, ref) => {
    const alignmentClasses = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
    };

    return (
      <div
        ref={ref}
        // US-075: Responsive padding and flex container for button alignment
        className={`px-4 sm:px-6 py-4 flex flex-col sm:flex-row gap-2 sm:gap-3 ${alignmentClasses[align]} ${borderTop ? 'border-t border-gray-200 dark:border-gray-700' : ''} ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

// CardDescription component (shadcn-style)
export interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {
  children: ReactNode;
}

export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className = '', children, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={`text-sm text-gray-500 dark:text-gray-400 ${className}`}
        {...props}
      >
        {children}
      </p>
    );
  }
);

CardDescription.displayName = 'CardDescription';
